/**
 * Console filter to silence noisy third-party service errors.
 * Shows first warning per service, then silences subsequent errors.
 */

// Service patterns to detect third-party errors
const SERVICE_PATTERNS = {
  amplitude: /amplitude|@amplitude/i,
  pendo: /\[pendo\]|pendo\.io|pendo\.js/i,
  zendesk: /\[zendesk\]|zdassets|zendesk/i,
} as const;

// Track which services have shown their first warning
const warnedServices = new Set<string>();

// Store original console methods
const originalConsole = {
  error: console.error,
  warn: console.warn,
};

function matchesService(args: unknown[]): string | null {
  const message = args.map(String).join(' ');
  for (const [service, pattern] of Object.entries(SERVICE_PATTERNS)) {
    if (pattern.test(message)) return service;
  }
  return null;
}

function filteredLog(method: 'error' | 'warn', ...args: unknown[]) {
  const service = matchesService(args);
  if (service) {
    if (warnedServices.has(service)) return; // Silence after first warning
    warnedServices.add(service);
    originalConsole.warn(`[${service}] Service error (further errors silenced):`, ...args);
    return;
  }
  originalConsole[method](...args);
}

/**
 * Install console filter to silence third-party service spam.
 * Call at app startup before any third-party SDKs initialize.
 */
export function installConsoleFilter() {
  console.error = (...args) => filteredLog('error', ...args);
  console.warn = (...args) => filteredLog('warn', ...args);
}

/**
 * Restore original console methods.
 */
export function uninstallConsoleFilter() {
  console.error = originalConsole.error;
  console.warn = originalConsole.warn;
}
